<template>
  <b-row v-if="!isLoading" class="justify-content-md-center">
    <b-col 
      cols="12" 
      md="12" 
      class="p-0"
      style="max-width: 820px">
      <!-- ** DELETE FORUM QUESTION MODAL ** -->
      <unshare-item-modal
        v-if="item"
        v-model="isUnshareModalVisible"
        model-type="forum"
        @remove-item="removeItem(item)"
        @modal-invisible="isUnshareModalVisible = false"
      />
      <!-- ** DELETE FORUM ANSWER MODAL ** -->
      <unshare-item-modal
        v-if="answerData"
        v-model="isUnshareAnswerModalVisible"
        model-type="answer"
        @remove-item="deleteAnswer(answerData.key)"
        @modal-invisible="handleAnswerModalClose"
      />
      <!-- ** FORUM QUESTION CLASSIFIERS MODAL ** -->
      <classifiers-modal
        v-if="isStaff && item && isClassifiersModalVisible"
        v-model="isClassifiersModalVisible"
        :item-key="item.key"
        morph-type="forums"
        model-type="forum"
        :classifiers="item.classifiers"
        v-on:profileUpdated="fetchForumClassifiers"
      />
      <!-- ** QUESTION ANSWER CLASSIFIERS MODAL ** -->
      <classifiers-modal
        v-if="isStaff && isClassifiersAnswerModalVisible"
        v-model="isClassifiersAnswerModalVisible"
        :item-key="currentAnswerKey"
        morph-type="answer"
        model-type="answer"
        :classifiers="currentAnswerClassifiers"
        v-on:profileUpdated="fetchAnswers"
      />

      <!-- ** FORUM QUESTION CARD ** -->
      <b-card 
        v-if="item && validForum.includes(id)" 
        no-body
      >
        <b-card-body class="no-padding">
          <!-- ** FORUM QUESTION IMAGE ** -->
          <b-img
            v-if="!editing"
            fluid
            :src="getImageSrc(item.bgURL)"
            class="d-block"
            style="
              max-height: 500px;
              width: 100%;
              border-top-left-radius: 1.2rem;
              border-top-right-radius: 1.2rem;
            "
          />
          <!-- ** FORUM QUESTION IMAGE UPLOAD ** -->
          <b-card-group
            v-if="editing"
            :label="$t('backoffice.onboarding-form.form.image')"
            label-for="image"
            class="d-flex w-100 justify-content-center"
          >
            <file-preview
              v-if="!editForm.bgURL && !image"
              :src="Placeholder"
              mime-type="image"
              style="height:300px; width:80%; margin-bottom: 20px; border-radius: 17px;"
            />
            <file-preview
              v-if="editForm.bgURL"
              :src="getImageSrc( editForm.bgURL)"
              :placeholder-image="Placeholder"
              mime-type="image"
              cancelable
              @cancel="editForm.bgURL = null"
            />
            <file-upload
              v-else
              v-model="image"
              type="image"
              cancelable
              :placeholder="$t('form-create-item.image-button')"
              :drop-placeholder="$t('form-create-item.drop-placeholder')"
              @cancel="image = null"
            />
          </b-card-group>

          <!-- ** FORUM QUESTION CONTENT ** -->
          <div class="p-2">
            <!-- ** FORUM QUESTION BADGE ** -->
            <b-badge
              v-if="showStatusBadge && status"
              class="my-50"
              :variant="getBadgeVariant(status)"
            >
              {{ getBadgeText(status) }}
            </b-badge>

            <!-- ** FORUM QUESTION NAME/TITLE ** -->
            <div v-if="!editing" class="d-flex mt-1 justify-content-between">
              <h3>
                {{ translate(item.name) }}
              </h3>
              <div style="width: 200px">
                <!-- Actions -->
                <b-dropdown 
                  v-if="canEdit(item) && !editing" 
                  id="dropdown-dropleft" 
                  variant="link" 
                  right 
                  class="text-primary edit-icon float-right ml-1" 
                  style="margin: -11px 0px !important;"
                  no-caret
                >
                  <!-- ** ACTIONS button ** -->
                  <template #button-content>
                    <feather-icon icon="SettingsIcon" size="20" class="text-primary" />
                  </template>
                  <!-- ** Edit forum question ** -->
                  <b-dropdown-item @click="openEdit">
                    <feather-icon icon="Edit2Icon" size="14" class="mr-50" />
                    {{ $t("edit-dropdown.title") }}
                  </b-dropdown-item>
                  <!-- ** Edit forum question classifiers ** -->
                  <b-dropdown-item 
                    v-if="isStaff"
                    @click="openClassifiersModal"
                  >
                    <feather-icon icon="LayersIcon" size="14" class="mr-50" />
                    {{ $t("members.edit.classifiers.name") }}
                  </b-dropdown-item>
                  <!-- ** Delete forum question ** -->
                  <b-dropdown-item 
                    v-if="canDelete(item) && !editing"
                    @click="isUnshareModalVisible = true"
                  >
                    <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
                    {{ $t("action.delete") }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>

            <!-- ** FORUM QUESTION NAME/TITLE EDIT ** -->
            <b-form-input
              v-else
              v-model="editForm.name"
              :placeholder="
                translate(item.name)
              "
              class="mt-1 font-medium-4 font-weight-bold"
            />

            <!-- SPACER -->
            <div class="float-right" style="width: 200px" />

            <!-- ** FORUM QUESTION CLASSIFIERS ** -->
            <div v-if="showClassifiers(item)" class="flex flex-wrap">
              <b-badge
                v-for="(classifier, index) in item.classifiers"
                :key="index"
                class="my-50 mr-50"
                variant="primary"
              >
                {{ translate(classifier.name) }}
              </b-badge>
            </div>
            
            <!-- ** FORUM QUESTION INFO: Date, num answers and num likes ** -->
            <b-card-text v-if="item && item.createdAt" class="text-muted mt-1 mb-2">
              {{ timestampToNow(item.createdAt) }}
              ·
              {{
                $tc("forums.answers", forumAnswersTotal, { count: forumAnswersTotal })
              }}
              ·
              {{
                $tc("social.likes", item.totalLikes || 0, { count: item.totalLikes || 0 })
              }}
            </b-card-text>

            <!-- ** DIVIDER LINE ** -->
            <hr>

            <!-- ** FORUM QUESTION AUTHOR INFO ** -->
            <b-row 
              v-if="item && item.ownedByMember" 
              id="question-author-info"
              cols="12" 
              class="pt-75 ml-0 pl-0"
            >
              <!-- Author -->
              <b-link
                :to="{
                  name: 'member-details',
                  params: { username: item.ownedByMember.username },
                }"
              >
                <b-avatar
                  size="38"
                  :src="getImageSrc(item.ownedByMember.avatarURL)"
                  :alt="item.ownedByMember.name || (item.name ? Object.values(item.name)[0] : '')"
                  class="user-avatar"
                />
              </b-link>

              <span v-if="item && item.ownedByMember" class="mb-1 ml-1">
                {{ item.ownedByMember.name }} {{ item.ownedByMember.surname }}
                <span
                  v-if="item.ownedByMember.headline"
                  class="ml-50 text-muted"
                >
                  {{ item.ownedByMember.headline }}
                </span>
              </span>
            </b-row>

            <!-- ** FORUM QUESTION DESCRIPTION ** -->
            <b-row>
              <!-- ** EDIT QUESTION DESCRIPTION ** -->
              <div
                v-if="editing"
                class="p-2"
              >
                <quill-editor
                  v-model="editForm.description"
                />
                <div class="d-flex justify-content-end mt-2">
                  <b-button
                    class="mt-n50 mr-75"
                    variant="primary"
                    @click="handleEditItem"
                  >
                    {{ $t("form.actions.save-changes") }}
                  </b-button>
                  <feather-icon
                    icon="XCircleIcon"
                    size="24"
                    class="text-primary edit-icon"
                    role="button"
                    @click="editing = !editing"
                  />
                </div>
              </div>

              <!-- ** QUESTION DESCRIPTION ** -->
              <div
                v-else
                class="p-2"
              >
                <!-- Description -->
                <b-card-text
                  v-if="item && translate(item.description)"
                  class=""
                >
                  <span
                    class="content-container"
                    v-html="translate(item.description)"
                  />
                </b-card-text>
              </div>
            </b-row>

            <!-- ** FORUM QUESTION ACTIONS ** -->
            <b-row class="d-flex">
              <!-- Like forum question -->
              <LikeButton
                :liked="item && item.likedByMember"
                :count="item ? (item.totalLikes || 0) : 0"
                :size="28"
                item-type="forums"
                morph-type="forum"
                :item-key="id"
                stored-key="forum"
                @toggled="fetchData"
              />

              <!-- Create forum answer -->
              <b-link href="#commentForm" class="text-dark">
                <span
                  role="button"
                  class="pointer"
                >
                  <feather-icon size="20" icon="EditIcon" class="mr-25" />
                  {{ $t("forums.icons.create-answer") }}
                </span>
              </b-link>
            </b-row>
          </div>
        </b-card-body>
      </b-card>

      <!-- ** ANSWERS ** -->
      <b-card 
        v-if="hasAnswers" 
        class="answers-container p-0" 
        no-body
      >
        <!-- Header -->
        <h5 class="pt-2 pb-1 pl-2 pb-0">
          {{ $tc("forums.answers", forumAnswersTotal, { count: forumAnswersTotal }) }}
        </h5>

        <hr class="m-0">

        <!-- Answers -->
        <div class="px-2">
          <!-- Answer loop -->
          <div
            v-for="(answer, index) in forumAnswersAll"
            :key="answer.key"
            class="p-2"
            :class="index != 0 ? ' border-top' : ''"
          >
            <!-- Answer main title -->
            <h3>
              {{ answer.answerToUpdate }}
            </h3>

            <!-- Answer header: author and date-->
            <b-row class="d-flex justify-content-between align-items-center">
              <!-- Author: avatar and name -->
              <div class="d-flex align-items-center">
                <b-link
                  v-if="answer.ownedByMember"
                  :to="{
                    name: 'member-details',
                    params: { 
                      username: answer.ownedByMember.username 
                    },
                  }"
                  class="d-flex align-items-center"
                >
                  <!-- Avatar -->
                  <b-avatar
                    size="32"
                    :src="getImageSrc(answer.ownedByMember.avatarURL)"
                    :alt="answer.ownedByMember.name"
                    class="user-avatar"
                  />
                  <!-- Name and headline -->
                  <div class="ml-2">
                    <div>
                      {{ answer.ownedByMember.name }}
                      {{ answer.ownedByMember.surname }}
                    </div>
                    <div
                      v-if="answer.ownedByMember.headline"
                      class="text-muted small"
                    >
                      {{ answer.ownedByMember.headline }}
                    </div>
                  </div>
                </b-link>
              </div>

              <!-- Date -->
              <span 
                v-if="answer.createdAt"
                class="text-muted"
              >
                {{ timestampToNow(answer.createdAt) }}
              </span>
            </b-row>

            <!-- Answer description -->
            <b-row>
              <div 
                v-if="editingAnswer && answerKey === answer.key"
              >
                <quill-editor
                  v-model="editAnswerForm"
                />
                <div class="d-flex justify-content-end mt-2">
                  <b-button
                    class="mt-n50 mr-75"
                    variant="primary"
                    @click="handleEditAnswer"
                  >
                    {{ $t("form.actions.save-changes") }}
                  </b-button>
                  <feather-icon
                    icon="XCircleIcon"
                    size="24"
                    class="text-primary edit-icon"
                    role="button"
                    @click="editingAnswer = !editingAnswer"
                  />
                </div>
              </div>
              <div v-else>
                <!-- Answer description -->
                <p
                  v-if="answer.description"
                  class="my-2"
                  v-html="translate(answer.description)"
                />
              </div>
            </b-row>

            <!-- Answer classifiers -->
            <div
              v-if="showClassifiers(answer)" 
              class="pl-0"
            >
              <b-badge
                v-for="(classifier, index) in answer.classifiers"
                :key="index"
                class="my-50 mr-50"
                variant="primary"
              >
                {{ translate(classifier.name) }}
              </b-badge>
            </div>

            <!-- Answer moderation -->
            <b-row>
              <moderation-section
                :content="answer"
                morph-type="forums"
                item-type="forumAnswers"
                item-type-name="answer"
                :parent-key="item && item.key"
                @moderation-complete="handleModerationComplete"
              />
            </b-row>

            <!-- All members actions: reply, likes, comments, check best answer and vote answer +/- -->
            <b-row class="d-flex py-50 ">
              <!-- Button open input new comment  -->
              <div
                role="button"
                class="hover-button"
                @click="toggleComments(answer)"
              >
                <feather-icon
                  icon="CornerUpLeftIcon"
                  size="18"
                  class="mr-50"
                />
                <span class="mb-0">
                  {{ $t("social.reply") }}
                </span>
              </div>

              <!-- Like forum answer -->
              <span
                class="hover-button mx-50"
                role="button"
                @click="toggleLike('forumAnswers', 'answer', answer.key, answer)"
              >
                <LikeButton
                  :liked="answer.likedByMember"
                  :count="answer.totalLikes"
                  :size="28"
                  item-type="forumAnswers"
                  morph-type="answer"
                  :item-key="answer.key"
                  @toggled="fetchAnswers"
                />
              </span>

              <!-- Comments icon and counter -->
              <span
                class="hover-button mx-50"
                role="button"
                @click="toggleComments(answer)"
              >
                <feather-icon
                  role="button"
                  size="20"
                  icon="MessageSquareIcon"
                  class="mr-25"
                />
                <!-- comments counter -->
                <span>
                  {{ answer.commentCount || 0 }}
                </span>
              </span>

              <!-- Check best answer -->
              <span
                v-if="showCheck && canShowCheck(answer)"
                class="check-best-answer hover-button mx-50"
                role="button"
                @click="selectBestAnswer(answer.key)"
              >
                <feather-icon
                  role="button"
                  size="20"
                  :stroke="answer.correctAnswer ? 'green' : 'lightgray'"
                  stroke-width="3"
                  icon="CheckIcon"
                  class="text-primary edit-icon"
                />
              </span>

              <!-- Vote answer -->
              <div 
                v-if="showVotes" 
                class="answer-votes d-flex align-items-center mb-75"
              >
                <!-- Vote positive answer -->
                <span
                  class="hover-button mx-50"
                  role="button"
                  @click="toggleVote('forumAnswers', 'answer', answer.key)"
                >
                  <feather-icon
                    :class="{ 'text-primary': answer.likedByMember }"
                    class="icon-clickable"
                    icon="ThumbsUpIcon"
                    size="18"
                  />
                </span>
                <!-- Number of votes -->
                <span
                  class="mx-0"
                  style="
                    font-weight: 600;
                    font-size: 1rem;
                  "
                >
                  {{ answer.totalLikes }}
                </span>
                <!-- Vote negative answer -->
                <span
                  class="hover-button mx-50"
                  role="button"
                  @click="!answer.likedByMember || toggleVote('forumAnswers', 'answer', answer.key)"
                >
                <feather-icon
                  class="icon-clickable"
                  icon="ThumbsDownIcon"
                  size="18"
                  />
                </span>
              </div>            
            </b-row>

            <!-- Management actions: delete, edit, add classifiers -->
            <b-row class="d-flex" style="margin-left: -1.5rem;">
              <!-- Delete answer -->
              <feather-icon
                v-if="canDelete(answer)"
                icon="Trash2Icon"
                size="20"
                class="text-primary edit-icon ml-1"
                role="button"
                @click="deleteAnswerClick(answer)"
              />

              <!-- Edit answer -->
              <feather-icon
                v-if="canEdit(answer)"
                icon="Edit2Icon"
                size="20"
                class="text-primary edit-icon ml-1"
                role="button"
                @click="selectAnswer(answer)"
              />

              <!-- Add classifiers to answer -->
              <feather-icon
                v-if="isStaff"
                icon="LayersIcon"
                size="20"
                class="text-primary edit-icon ml-1"
                role="button"
                @click="openClassifiersAnswerModal(answer)"
              />
            </b-row>

            <!-- Comments section to answer -->
            <div v-if="showComments.includes(answer.key)" class="mt-3">
              <comments-section
                :source-key="answer.key"
                :can-moderate="canModerateComments"
                morph-type="answer"
                item-type="comments"
                :embedded="true"
                :leaf="true"
                @comment-deleted="handleCommentDeleted(answer)"
              />
            </div>
              
          </div>  <!-- End of answer -->
        </div>

        <!-- Load more answers -->
        <div
          v-if="showLoadMore"
          role="button"
          class="pb-2 w-100 text-center text-primary"
          @click="handleLoadOfNewItems"
        >
          {{ $t("action.load-more") }}
        </div>
      </b-card>

      <!-- NO ANSWERS - Container without data: Placeholder -->
      <b-card v-else>
        <b-row class="horizontal-placeholder">
          <b-col cols="12">
            <img :src="Placeholder">
          </b-col>
          <b-col cols="12">
            <p class="text-primary">
              {{
                $t("available.message", {
                  itemName: $tc("forums.answer-title"),
                })
              }}
            </p>
          </b-col>
        </b-row>
      </b-card>

      <!-- ** CREATE ANSWER TO FORUM QUESTION ** -->
      <b-card id="commentForm" class="p-2">
        <!-- Form title -->
        <h5 class="mb-2">
          {{ $t("forums.your-reply") }}
        </h5>

        <!-- Form to create answer -->
        <b-form
          class="form-challenge-idea my-2"
          @submit.prevent="handleCreateForumAnswer"
        >
          <!-- Answer input textarea -->
          <quill-editor
            v-model="form.description"
            class="mb-2 w-100 float-left"
          />

          <div class="float-left mb-2">
            <!-- Submit button -->
            <b-button variant="primary" type="submit">
              {{ $t("send.button") }}
            </b-button>
          </div>
        </b-form>
      </b-card>

      <!-- Moderation Feedback Modal -->
      <b-modal
        v-model="isModerationFeedbackModalVisible"
        :title="$t('forums.moderation.feedback-modal.title')"
        centered
      >
        <b-form>
          <!-- AI Recommendation -->
          <div class="mb-2">
            <h6>{{ $t('forums.moderation.feedback-modal.ai-recommendation') }}</h6>
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="moderationAction === 'accept' ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'"
                size="18"
                class="mr-1"
              />
              <span :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'">
                {{ $t(`forums.moderation.feedback-modal.${moderationAction}-recommendation`) }}
              </span>
            </div>
          </div>

          <!-- Feedback for the User -->
          <b-form-group
            :label="$t('forums.moderation.feedback-modal.user-feedback-label')"
          >
            <b-form-textarea
              v-model="moderationFeedback.feedbackForUser"
              rows="3"
              :placeholder="$t('forums.moderation.feedback-modal.user-feedback-placeholder')"
            />
          </b-form-group>

          <!-- Agreement with AI Recommendation -->
          <b-form-group class="mb-2">
            <b-form-radio-group
              v-model="moderationFeedback.agreesWithAI"
              :options="[
                { text: $t('forums.moderation.feedback-modal.agree-with-ai'), value: true },
                { text: $t('forums.moderation.feedback-modal.disagree-with-ai'), value: false }
              ]"
            />
          </b-form-group>

          <!-- Feedback for the AI agent (only shown when disagreeing) -->
          <b-form-group
            v-if="moderationFeedback.agreesWithAI === false"
            :label="$t('forums.moderation.feedback-modal.ai-feedback-label')"
          >
            <b-form-textarea
              v-model="moderationFeedback.aiFeedback"
              rows="3"
              :placeholder="$t('forums.moderation.feedback-modal.ai-feedback-placeholder')"
            />
          </b-form-group>
        </b-form>

        <template #modal-footer>
          <b-button
            variant="outline-secondary"
            @click="isModerationFeedbackModalVisible = false"
          >
            {{ $t('forums.moderation.feedback-modal.cancel') }}
          </b-button>
          <b-button
            :variant="moderationAction === 'accept' ? 'success' : 'danger'"
            @click="submitModeration"
          >
            {{ $t(`forums.moderation.feedback-modal.${moderationAction}`) }}
          </b-button>
        </template>
      </b-modal>
    </b-col>

    <!-- Other forum threads and new forum card -->
    <b-col cols="12" md="3" style="max-width: 350px">
      <!-- New forum card -->
      <b-card v-if="canAdd">
        <h3 class="mb-1 mt-50">
          {{ $t("forums.new-post.title") }}
        </h3>
        <p>
          {{ $t("forums.new-post.text1") }}<!-- <br>
          {{ $t("forums.new-post.text2") }}-->
        </p>
        <b-button
          variant="primary"
          type="submit"
          class="w-100 my-50"
          @click="openModal"
        >
          {{ $t("forums.new-post.create") }}
        </b-button>
      </b-card>

      <!-- More forum threads list -->
      <div v-if="forumsList.length > 1" class="mt-3">
        <h5 class="section-label mb-2">
          {{ $t("forums.more-questions") }}
        </h5>
        <div v-for="forum in forumsList.slice(0, 5)" :key="forum.key">
          <b-link v-if="forum.key!==id" :to="goToForum(forum.key)">
            <h6
              role="button"
              class="mb-25 html-text-ellipsis-2"
            >
              {{ forum.name[currentLocale] || Object.values(forum.name)[0] }}
            </h6>
            <p v-if="forum.ownedByMember" class="text-muted">
              {{ forum.ownedByMember.name }} {{ forum.ownedByMember.surname }}
            </p>
            <hr>
          </b-link>
        </div>
      </div>
    </b-col>

    <!-- Create Forum -->
    <!-- TODO: create create item component -->
    <b-modal
      id="modal-create-forums"
      :title="$t('Forum')"
      cancel-variant="outline-secondary"
      :ok-title="$t('form.actions.save')"
      :cancel-title="$t('form-create-item.cancel')"
      centered
      size="lg"
      @ok="handleCreateForum"
    >
      <b-form>
        <b-form-group>
          <label for="name"> {{ $t("form-create-item.title") }}:</label>
          <b-form-input
            id="name"
            v-model="forum.name"
            :placeholder="$t('form.placeholder')"
            type="text"
          />
        </b-form-group>
        <b-form-group>
          <label class="mt-2" for="description">{{ $t("form-create-item.description") }}:</label>
          <quill-editor v-model="forum.description" />
        </b-form-group>

        <b-col color="12" md="6">
          <b-form-group label="Banner" label-for="banner">
            <file-upload
              v-model="imageForum"
              type="image"
              cancelable
              :placeholder="$t('form-create-item.image')"
              :drop-placeholder="$t('form-create-item.drop-placeholder')"
              @cancel="imageSrc = null"
            />
          </b-form-group>
        </b-col>
      </b-form>
    </b-modal>
  </b-row>
</template>

<style scoped>
/* Remove padding from dropdown toggle button */
:deep(.dropdown-toggle.btn-link) {
  padding: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1;
}
/* Adjust the icon alignment */
:deep(.dropdown-toggle.btn-link .feather) {
  margin: 0 !important;
  vertical-align: top;
}
</style>

<script>
import { quillEditor } from 'vue-quill-editor';
import moment from 'moment';
import { getImageResource } from '@/@core/utils/image-utils';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import Placeholder from '@/assets/images/placeholders/light/forums.svg';
import Vue from 'vue';
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue';
import UnshareItemModal from '@/@core/components/modal/UnshareItemModal.vue';
import ClassifiersModal from '@/@core/components/modal/ClassifiersModal.vue';
import FileUpload from '@core/components/files/FileUpload.vue';
import { checkPermissions } from '@/@core/utils/roles-utils';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import FilePreview from '@core/components/files/FilePreview.vue';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';
import { CommentsSection } from '@/views/apps/comments/components';
import LikeButton from '@/components/LikeButton.vue';

export default {
  name: 'ForumDetails',
  components: {
    quillEditor,
    UnshareItemModal,
    FileUpload,
    FilePreview,
    ModerationSection,
    CommentsSection,
    LikeButton,
    ClassifiersModal,
    },
	  mixins: [ToastNotificationsMixin],

  data() {
    return {
      isLoading: true,
      validForum: [],
      // forum var
      editing: false,
      editForm: { name: '', description: '', bgURL: null },
      lastLoadedPage: 1,
      isUnshareModalVisible: false,
      isClassifiersModalVisible: false,
      isClassifiersAnswerModalVisible: false,
      currentAnswerKey: null,
      currentAnswerClassifiers: [],
      imageForum: null,
      imageSrc: null,
      image: null,
      forum: { name: '', description: '' },
      // answers var
      editingAnswer: false,
      form: { name: '', description: '' },
      editAnswerForm: {},
      lastLoadedPageAnswers: 1,
      isUnshareAnswerModalVisible: false,
      selectedAnswer: {},
      answerKey: '',
      answerData: null,
      showComments: [],
      // comments var
      newComment: '',
      status: 42658,
      isLoadingNextPage: false,
      // Moderation
      isModerationFeedbackModalVisible: false,
      moderationAction: null,
      selectedAnswerForModeration: null,
      moderationFeedback: {
        feedbackForUser: '',
        aiFeedback: '',
        agreesWithAI: null
      },
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    forumsData() {
      return this.$store.getters.forums;
    },
    item() {
      if (this.forumsData.forum) {
        const item = this.forumsData.forum.unpaginated[0];
        if (item && item.key) {
          this.validItem(item.key);
          return item;
        }
      }
      return null;
    },
    forumAnswers() {
      return this.$store.getters.forumAnswers;
    },
    forumAnswersPaginated() {
      return this.$store.getters.forumAnswers.paginated;
    },
    forumAnswersAll() {
      let allForumAnswers = [];
      // console.log('this.forumAnswersPaginated', this.forumAnswersPaginated);
      if(this.forumAnswersPaginated){
        for(const page of Object.values(this.forumAnswersPaginated)) {
          // console.log('page', page);
          for(const item of page){
            allForumAnswers.push(item);
          }
        }
      }
      return allForumAnswers;
    },
    forumAnswersUnpaginated() {
      // console.log('this.forumAnswers', this.forumAnswers)
      return this.forumAnswers.unpaginated;
    },
    forumAnswersMeta() {
      return this.forumAnswers.meta;
    },
    hasAnswers() {
      return this.forumAnswersAll.length > 0;
      // return this.forumAnswers.unpaginated.length > 0;
    },
    forumAnswersTotal() {
      if (this.forumAnswersMeta) {
        return this.forumAnswersMeta?.total;
      } return 0;
    },
    forumsList() {
      if (this.forumsData.list) {
        return this.forumsData.list?.unpaginated;
      } return [];
    },
    collective() {
      return this.$store.state.collective.collective;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    loggedMemberRoles() {
      return this.$store.getters.loggedMemberRoles;
    },
    loggedUser() {
      return this.$store.getters.loggedUser;
    },
    loggedMember() {
      return this.$store.getters.loggedMember;
    },
    isNexos() {
      return process.env.VUE_APP_CUSTOMER === 'nexos';
    },
    showVotes() {
      return !this.isNexos;
    },
    showCheck() {
      return !this.isNexos;
    },
    showStatusBadge() {
      return !this.isNexos;
    },
    canAdd() {
      return (
        this.isStaff
        || checkPermissions(
          'create',
          'forum',
          this.loggedMemberRoles,
          this.$store.getters.currentCollective,
        )
      );
    },
    canModerateComments() {
      return this.isStaff || checkPermissions(
        'manage',
        'comment',
        this.loggedMemberRoles,
        this.$store.getters.currentCollective,
      );
    },

    /*     canDelete() {
      return (
        this.loggedUser.key === this.item?.ownedByMember?.key
        || this.isStaff
        || checkPermissions(
          'delete',
          'forum',
          this.loggedMemberRoles,
          this.$store.getters.currentCollective,
        )
      );
    }, */
    Placeholder() {
      return Placeholder;
    },
    appId() {
      return 28;
    },
    apps() {
      return this.$store.getters.apps;
    },
    app() {
      if (this.apps) {
        return this.apps.apps[this.appId] || {};
      }
      return {};
    },
    availableForumClassifiers() {
      const availableForumClassifiers = this.$store.getters.classifiers["forum"];
      console.log('availableForumClassifiers() - availableForumClassifiers:', availableForumClassifiers);
      if (availableForumClassifiers) {
        const unpaginatedClassifiers = availableForumClassifiers.unpaginated;
        console.log('availableForumClassifiers() - unpaginatedClassifiers for forum:', unpaginatedClassifiers);
        return unpaginatedClassifiers;
      } else {
        console.log('availableForumClassifiers() - no classifiers for forum');
        return [];
      }
    },
    availableAnswersClassifiers() {
      const availableAnswersClassifiers = this.$store.getters.classifiers["answer"];
      console.log('availableAnswersClassifiers() - availableAnswersClassifiers:', availableAnswersClassifiers);
      if (availableAnswersClassifiers) {
        const unpaginatedClassifiers = availableAnswersClassifiers.unpaginated;
        console.log('availableAnswersClassifiers() - unpaginatedClassifiers for answers:', unpaginatedClassifiers);
        return unpaginatedClassifiers;
      } else {
        console.log('availableAnswersClassifiers() - no classifiers for forum answers');
        return [];
      }
    },
    showLoadMore() {
      console.log('showLoadMore() - forumAnswersAll.length:', this.forumAnswersAll.length);
      console.log('showLoadMore() - forumAnswersTotal:', this.forumAnswersTotal);
      console.log('showLoadMore() - isLoadingNextPage:', this.isLoadingNextPage);
      return this.forumAnswersAll.length < this.forumAnswersTotal && !this.isLoadingNextPage;
    },
  },
  watch: {
    imageForum(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.base64Encode(newValue)
            .then((value) => {
              this.imageSrc = value;
            })
            .catch(() => {
              this.imageSrc = null;
            });
        } else {
          this.imageSrc = null;
        }
      }
    },
    async validForum(value) {
      if (!value.includes(this.id)) {
			 this.isLoading = true;
			 await this.fetchData();
			 if (Object.keys(this.item).length > 0) {
			 // this.validItem(this.item.key);
				 this.updateBreadcrumbs();
				 await this.fetchAnswers();
			 }
			 if (this.forumsList.length === 0) {
				 await this.fetchForumList();
			 }
			 this.isLoading = false;
		 }
    },
  },
  async created() {
    this.isLoading = true;
    await this.fetchData();
    if (Object.keys(this.item).length > 0) {
      // this.validItem(this.item.key);
      this.updateBreadcrumbs();
      await this.fetchAnswers();
    }
    if (this.forumsList.length === 0) {
      await this.fetchForumList();
    }
    await this.fetchForumClassifiers();
    this.isLoading = false;
  },
  methods: {
    base64Encode(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    canEdit(element) {
      return (
        this.isStaff
        || ((this.loggedUser.key === element?.ownedByMember?.key || this.loggedMember.key === element?.createdBy?.key)
         && checkPermissions(
           'update',
           'forum',
           this.loggedMemberRoles,
           this.$store.getters.currentCollective,
         ))
      );
    },
    canDelete(element) {
      return (
        this.isStaff
        || ((this.loggedUser.key === element?.ownedByMember?.key || this.loggedMember.key === element?.createdBy?.key)
         && checkPermissions(
           'delete',
           'forum',
           this.loggedMemberRoles,
           this.$store.getters.currentCollective,
         ))
      );
    },
    canShowCheck(answer) {
      return answer.correctAnswer || (this.canEdit && this.item && this.item.status !== 42659 && this.status !== 42659);
    },
    getImageSrc(url) {
      return getImageResource(url);
    },
    getBadgeVariant(status) {
      if (!status || status === 42658) {
        return 'warning';
      } if (status === 42659 || status === 42736) {
        return 'success';
      }
      return '';
    },
    getBadgeText(status) {
      if (!status || status === 42658) {
        return this.$t('forums.open');
      } if (status === 42659 || status === 42736) {
        return this.$t('forums.closed');
      }
      return '';
    },
    translate(field) {
      return translateTranslationTable(
        this.currentLocale,
        field,
      );
    },
    timestampToNow(timestamp) {
      moment.locale(this.currentLocale);
      return moment(timestamp).fromNow();
    },
    openEdit() {
      if (!this.item) return;
      this.editForm.name = this.translate(this.item.name);
      this.editForm.description = this.translate(this.item.description);
      this.editForm.bgURL = this.item.bgURL;
      this.editing = !this.editing;
    },
    openClassifiersModal() {
      this.isClassifiersModalVisible = true;
    },
    selectAnswer(answer) {
      this.editAnswerForm = this.translate(answer.description);
      this.editingAnswer = !this.editingAnswer;
      this.selectedAnswer = answer;
      this.answerKey = answer.key;
    },

    openModal() {
      this.$bvModal.show('modal-create-forums');
    },
    goToForum(forumKey) {
      return {
        name: 'forum',
        params: { id: forumKey },
      };
    },
    // GETS
    async fetchData() {
      this.isLoading = true;
      await this.$store.dispatch('getItems', {
        itemType: 'forums',
        storedKey: 'forum',
        page: 1,
        forceAPICall: true,
        requestConfig: {
          count: 5,
          forumKeys: this.id,
        },
      });
      this.isLoading = false;
    },
    async fetchForumList() {
      this.isLoading = true;
      await this.$store.dispatch('getItems', {
        itemType: 'forums',
        storedKey: 'list',
        forceAPICall: true,
        page: this.lastLoadedPage,
        perPage: 10,
      });
      this.isLoading = false;
    },
    async fetchAnswers() {
      if (!this.item || !this.item.key) return;
      const response = await this.$store.dispatch('getItems', {
        itemType: 'forumAnswers',
        morphType: 'forums',
        perPage: 16,
        forceAPICall: true,
        page: this.lastLoadedPageAnswers,
        requestConfig: {
          forumKeys: this.item.key,
        },
      });
      let answers = [];
      this.showComments = [];
      answers = [...response.data];
      // CommentsSection component will handle its own data fetching
    },
    async fetchForumClassifiers() {
      await this.$store.dispatch('getItems', {
        storedKey: 'forum',
        itemType: 'backoffice/classifiers',
        customName: 'classifiers',
        forceAPICall: true,
        page: 1,
        requestConfig: {
          forumKey: this.item?.key,
        },
      });
      this.isLoading = false;
      this.updateBreadcrumbs();
    },
    async fetchAnswersClassifiers(answerKey) {
      await this.$store.dispatch('getItems', {
        storedKey: 'answer',
        itemType: 'backoffice/classifiers',
        customName: 'classifiers',
        forceAPICall: true,
        page: 1,
        requestConfig: {
          answerKey,
        },
      });
      this.isLoading = false;
      this.updateBreadcrumbs();
    },
    // POST
    async handleCreateForumAnswer() {
      if (!this.form.description || !this.item || !this.item.key) {
        return;
      }
      const requestConfig = {
        name: {},
        description: {},
        forumKey: this.item.key,
      };
      try {
        requestConfig.name[this.currentLocale] = this.form.name;
        requestConfig.description = this.form.description;

        // Create new forum answer
        await this.$store.dispatch('createItem', {
          item: {
            itemType: 'forumAnswers',
            orderByDate: 1,
            requestConfig,
          },
        });
        // Reset form
        this.form = { name: '', description: '' };
        // Notify success
        this.notifySuccess(this.$t('success-message.general-success-create'));
        // Fetch answers
        await this.fetchAnswers();
      } catch {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },
    async handleCreateForum() {
      if (!this.forum.name) {
        return;
      }
      try {
        const requestConfig = { name: {}, description: {} };
        requestConfig.name[this.currentLocale] = this.forum.name;
        requestConfig.description[this.currentLocale] = this.forum.description;
        const response = await this.$store.dispatch('createItem', {
          item: {
            itemType: 'forums',
            storedKey: 'list',

            requestConfig,
          },
          file: this.imageForum,
        });
        this.forum = { name: '', description: '' };
        this.imageForum = null;
        this.$router.push({ name: 'forum', params: { id: response.data.key } });
        this.updateBreadcrumbs();
        this.notifySuccess(this.$t('success-message.general-success-create'));
      } catch {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },
    // async postComment(answer) {
    //   await this.$store.dispatch('postComment', {
    //     morphType: 'answer',
    //     key: answer.key,
    //     comment: this.newComment,
    //   });
    //   this.newComment = '';
    //   answer.commentCount++;
    //   this.getComments(answer);
    //   this.notifySuccess(this.$t('success-message.general-success-create'));
    //   if (!this.showComments.includes(answer.key)) {
    //     this.showComments.push(answer.key);
    //   }
    // },
    // PUT
    async handleEditItem() {
      if (!this.item) return;
      try {
        const file = {};
        const itemDelete = {};
        const editedName = {};
        const editedDescription = {};
        if (this.image) {
          file.bgURL = this.image;
        } else if (this.editForm.bgURL !== null) {
          file.bgURL = this.editForm.bgURL;
        } else {
          file.bgURL = null;
          itemDelete.deleteBgURL = true;
        }
        editedName[this.currentLocale] = this.editForm.name;
        editedDescription[this.currentLocale] = this.editForm.description;

        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'forums',
            storedKey: 'forum',
            requestConfig: {
              forumKey: this.id,
              key: this.id,
              name: editedName,
              description: editedDescription,
              ...itemDelete,
            },
          },
          file,
        });
        this.item.name = editedName;
        this.item.description = editedDescription;
        this.item.bgURL = file.bgURL;
        this.editing = !this.editing;
        this.notifySuccess(this.$t('success-message.general-success-edit'));
        this.updateBreadcrumbs();
      } catch {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },
    async handleEditAnswer() {
      try {
        const editedAnswer = {};
        const { comments } = this.selectedAnswer;
        editedAnswer[this.currentLocale] = this.editAnswerForm;
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: 'forumAnswers',
            morphType: 'forums',
            requestConfig: {
              forumKey: this.item.key,
              answerKey: this.answerKey,
              description: this.editAnswerForm,
            },
          },
        });
        response.data.comments = comments;
        response.data.showReply = this.selectedAnswer.showReply;
        this.answerKey = '';
        this.selectedAnswer = {};
        this.notifySuccess(this.$t('success-message.general-success-edit'));
        this.editingAnswer = !this.editingAnswer;
      } catch {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },

    // DELETE
    async removeItem(item) {
      try {
        await this.$store.dispatch('unshareForum', {
          item: {
            itemType: 'forums',
            storedKey: 'list',
            forumKey: item.key,
          },
        });
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: this.$t('unshare.toast-messages.success', { item: 'Forum' }),
            icon: 'AlertTriangleIcon',
            variant: 'success',
          },
        });
        this.isUnshareModalVisible = false;
				 return this.$router.replace({
          name: 'forums',
        });
      } catch (err) {
        this.notifyError(this.$t('error-message.general-error'));
      }
    },
    async deleteAnswer(answerKey) {
      if (!this.item || !this.item.key || !answerKey) return;
      await this.$store.dispatch('unshareForum', {
        item: {
          itemType: 'forumAnswers',
          forumKey: this.item.key,
          answerKey,
        },
      });
      if (this.item.answerCount) {
        --this.item.answerCount;
      }
      this.isUnshareAnswerModalVisible = false;
      this.notifySuccess(this.$t('success-message.general-success-delete'));
      await this.fetchAnswers();
    },

    // TOGGLES
    async toggleLike(type, morphType, key, answer, storedKey) {
      await this.$store.dispatch('toggleLike', {
        itemType: type,
        morphType,
        key,
        storedKey,
      });
      if (type === 'forumAnswers') {
        this.fetchAnswers(this.item);
      }
      // CommentsSection component will handle comment updates
    },
    async toggleVote(type, morphType, key, answer, storedKey) {
      await this.$store.dispatch('toggleVote', {
        itemType: type,
        morphType,
        key,
        storedKey,
      });
      if (type === 'forumAnswers') {
        this.fetchAnswers(this.item);
      }
      // CommentsSection component will handle comment updates
    },
   	toggleComments(answer) {
      if (this.showComments.includes(answer.key)) {
        const index = this.showComments.findIndex((key) => key === answer.key);
        this.showComments.splice(index, 1);
      } else {
        this.showComments.push(answer.key);
      }
    },
    validItem(itemKey) {
      if (!itemKey) return;
      if (this.validForum.length === 0) {
        this.validForum.push(itemKey);
      } else if (itemKey !== this.id) {
        this.validForum = [];
      }
    },
    async toggleReply(answer) {
      if (!answer.showReply) this.resetInputsReplies();
      if (!answer.showReply) {
        const answerToUpdate = this.forumAnswersAll.find(
          (element) => element.key === answer.key,
        );
        Vue.set(answerToUpdate, 'showReply', true);
      } else if (answer.showReply) {
        answer.showReply = false;
      }
    },
    async selectBestAnswer(answerKey) {
      if (!this.item || !this.item.key) return;
      await this.$store.dispatch('postItem', {
        type: 'bestAnswers',
        requestConfig: {
          forumKey: this.item.key,
          answerKey,
        },
      });
      this.closeForum();
    },
    closeForum() {
      if (!this.item || !this.item.key) return;
      this.$store
        .dispatch('postItem', {
          type: 'forumStatus',
          requestConfig: {
            status: 42659,
            forumKey: this.item.key,
          },
        })
        .then((response) => {
          this.status = 42659;
          // this.answers.data = [];
          this.fetchAnswers();
          this.$store.commit('EDIT_ITEM', {
            itemType: 'forums',
            storedKey: 'forum',
            data: response.data,
          });
          this.getBadgeVariant(42659);
          this.getBadgeText(42659);
        });
    },
    resetInputsReplies() {
      this.forumAnswersAll.forEach((answer) => {
        if (answer.showReply) Vue.set(answer, 'showReply', false);
      });
    },
    updateBreadcrumbs() {
      const breadcrumbs = [
        {
          text:
          translateTranslationTable(
            this.$store.state.locale.currentLocale,
            this.app.customizationName
            ,
          ) || this.$t('Forum'),
          to: { name: 'forums' },
        },

      ];
      if (this.item && this.item.name) {
        breadcrumbs.push({
          text: this.translate(this.item.name) || 'forums.breadcrumb-text.forum-details',
          active: true,
        });
      }
      this.$store.commit('app/SET_BREADCRUMBS', breadcrumbs);
    },
    async handleLoadOfNewItems() {
      // console.log('this.forumAnswersAll', this.forumAnswersAll);
      // console.log('this.forumAnswersMeta', this.forumAnswersMeta)
      const total = this.forumAnswersMeta?.total;
      // console.log('this.isLoading', this.isLoading);
      // console.log('this.forumAnswersAll.length', this.forumAnswersAll.length);
      // console.log('total', total);
      if (!this.isLoading && this.forumAnswersAll.length < total) {
        this.lastLoadedPageAnswers ++;
        this.isLoadingNextPage = true;
        await this.fetchAnswers({ 
          forceApiCall: true
         });
        this.isLoadingNextPage = false;
      }
    },
    async getMoreAnswers(){
      this.lastLoadedPageAnswers ++;
      await this.fetchAnswers();
    },

    handleCommentDeleted(answer) {
      if (answer.commentCount > 0) {
        answer.commentCount--;
      }
    },
    async handleModerationComplete({ action, contentKey }) {
      if (action === 'reject') {
        await this.deleteAnswer(contentKey);
      }
      await this.fetchAnswers();
    },
    showClassifiers(item) {
      return item.classifiers && item.classifiers.length > 0;
    },
    openClassifiersAnswerModal(answer) {
      this.currentAnswerKey = answer.key;
      this.currentAnswerClassifiers = answer.classifiers;
      this.isClassifiersAnswerModalVisible = true;
    },
    deleteAnswerClick(answer) {
      this.answerData = answer;
      this.isUnshareAnswerModalVisible = true;
    },
    handleAnswerModalClose() {
      this.isUnshareAnswerModalVisible = false;
      this.answerData = null;
    },
  },
};
</script>
<style>
.content-container::v-deep img {
  max-width: 100% !important;
  height: auto !important;
}
</style>